import React, { useState, useEffect } from 'react';
import { loadTaxStore, getItemOverride, setItemTaxes, currentOwnerId, hydrateTaxStore } from './taxStore';
import { TAXES_VISIBLE } from '../../config/featureFlags';

/**
 * Per-item tax assignment, shared by the service, product and voucher
 * editors. Self-contained: reads and writes the creator-scoped tax store
 * directly, so the host editor's save payload is untouched (no backend
 * coupling). "Use my default rates" follows the Taxes page; unticking it
 * reveals per-rate choices, where none ticked means explicitly tax-free.
 */

const ink = '#1A1A1E';
const muted = '#8E8E93';

const TaxPicker = ({ domain, itemId }) => {
  const ownerId = currentOwnerId();
  const store = loadTaxStore(ownerId);
  const enabledTaxes = store.taxes.filter((t) => t.enabled);
  const [override, setOverride] = useState(() => getItemOverride(ownerId, domain, itemId));
  const [, forceRefresh] = useState(0);
  // Hydrate the creator's taxes when the picker mounts (it can appear on
  // item editors before the Taxes page has ever been opened).
  useEffect(() => {
    let alive = true;
    hydrateTaxStore(ownerId).then(() => {
      if (!alive) return;
      setOverride(getItemOverride(ownerId, domain, itemId));
      forceRefresh((n) => n + 1);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  // Taxes off for this seller → the picker would promise something the till
  // does not do. Turn it on from the Taxes page first.
  if (!itemId || store.enabled !== true || enabledTaxes.length === 0) return null;

  const usingDefaults = override === null;
  const defaults = enabledTaxes.filter((t) => t.appliesTo[domain]);

  const applyOverride = (taxIds) => {
    setItemTaxes(ownerId, domain, itemId, taxIds);
    setOverride(taxIds);
  };

  const toggleDefaults = () => {
    if (usingDefaults) {
      applyOverride(defaults.map((t) => t.id));
    } else {
      applyOverride(null);
    }
  };

  const toggleTax = (taxId) => {
    const current = override || [];
    applyOverride(current.includes(taxId) ? current.filter((id) => id !== taxId) : [...current, taxId]);
  };

  return (
    <div className="sp-field">
      <label className="sp-label">Tax</label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={usingDefaults}
          onChange={toggleDefaults}
          style={{ width: 17, height: 17, accentColor: '#29282D' }}
        />
        <span style={{ fontSize: 13.5, color: ink }}>
          Use my default rates
          {usingDefaults && (
            <span style={{ color: muted }}>
              {defaults.length ? ` (${defaults.map((t) => `${t.name} ${t.rate}%`).join(', ')})` : ' (none apply)'}
            </span>
          )}
        </span>
      </label>

      {!usingDefaults && (
        <div style={{ paddingLeft: 27 }}>
          {enabledTaxes.map((t) => (
            <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={(override || []).includes(t.id)}
                onChange={() => toggleTax(t.id)}
                style={{ width: 16, height: 16, accentColor: '#29282D' }}
              />
              <span style={{ fontSize: 13, color: ink }}>{t.name} <span style={{ color: muted }}>{t.rate}%</span></span>
            </label>
          ))}
          {(override || []).length === 0 && (
            <p className="sp-hint" style={{ margin: '4px 0 0' }}>No tax will apply to this item.</p>
          )}
        </div>
      )}
    </div>
  );
};

// Feature pulled from this release — embedded pickers render nothing. Wrapper
// keeps the hooks inside TaxPicker unconditional (rules-of-hooks safe).
const GatedTaxPicker = (props) => (TAXES_VISIBLE ? <TaxPicker {...props} /> : null);
export default GatedTaxPicker;
